import { z } from 'zod'
import { toast } from 'sonner'
import axios from '@/lib/axios'
import { useForm } from 'react-hook-form'
import { Card } from '@/components/ui/card'
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Video as LucideVideo } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import {
  FeedType,
  FeedPrivacy,
  type FeedCreatePostPayload,
  type SharedContent,
  MediaItem,
} from '@/types/feed.type'

const feedSchema = z.object({
  type: z.nativeEnum(FeedType),
  content: z.string().optional(),
  mediaItems: z
    .array(
      z.object({
        type: z.enum(['image', 'video']),
        url: z.string().url(),
        thumbnailUrl: z.string().optional(),
        caption: z.string().optional(),
      }),
    )
    .optional(),
  sharedContent: z
    .object({
      type: z.enum(['article', 'livestream', 'feed']),
      contentId: z.string(),
      title: z.string().optional(),
      description: z.string().optional(),
      previewImageUrl: z.string().optional(),
      originalUrl: z.string().optional(),
      originalAuthor: z
        .object({
          userId: z.string(),
          displayName: z.string(),
          imageUrl: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  privacy: z.nativeEnum(FeedPrivacy),
  taggedUsers: z
    .array(
      z.object({
        userId: z.string(),
        displayName: z.string(),
      }),
    )
    .optional(),
  hashtags: z.array(z.string()).optional(),
})

type FeedFormType = z.infer<typeof feedSchema>

const defaultValues: Partial<FeedFormType> = {
  type: FeedType.TEXT,
  content: '',
  privacy: FeedPrivacy.PUBLIC,
  hashtags: [],
}

interface FeedFormDialogProps {
  open: boolean
  onClose: () => void
  sharedContent?: SharedContent
  videoUrl?: string
}

export default function FeedFormDialog({
  open,
  onClose,
  sharedContent,
  videoUrl,
}: FeedFormDialogProps) {
  const form = useForm<FeedFormType>({
    resolver: zodResolver(feedSchema),
    defaultValues,
  })
  const [loading, setLoading] = useState(false)

  // Handle shared content when dialog opens
  useEffect(() => {
    if (open && sharedContent) {
      form.setValue('type', FeedType.SHARED_LIVESTREAM)
      form.setValue('sharedContent', sharedContent)
    }
  }, [open, sharedContent, form])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset(defaultValues)
    }
  }, [open, form])

  // handle hashtag input
  const [hashtagInput, setHashtagInput] = useState('')
  const handleAddHashtag = () => {
    const tag = hashtagInput.trim()
    // ถ้า tag ขึ้นต้นด้วย # ให้ลบ # ตัวแรกออก
    let cleanTag = tag
    if (tag.startsWith('#')) {
      cleanTag = tag.slice(1)
    }

    if (cleanTag && !form.getValues('hashtags')?.includes(cleanTag)) {
      form.setValue('hashtags', [
        ...(form.getValues('hashtags') || []),
        cleanTag,
      ])
      setHashtagInput('')
    }
  }

  const handleRemoveHashtag = (tag: string) => {
    form.setValue(
      'hashtags',
      (form.getValues('hashtags') || []).filter((t) => t !== tag),
    )
  }

  // handle submit
  const onSubmit = async (values: FeedFormType) => {
    setLoading(true)
    try {
      // Clean up payload ตาม type
      let payload: Partial<FeedCreatePostPayload>
      const hashtags = values.hashtags || []
      const taggedUsers = values.taggedUsers || []

      // Add videoUrl to mediaItems if provided
      const mediaItems: MediaItem[] = []
      if (videoUrl) {
        mediaItems.push({
          type: 'video',
          url: videoUrl,
        })
      }

      if (values.type === FeedType.TEXT) {
        payload = {
          ...values,
          mediaItems: videoUrl ? mediaItems : [],
          taggedUsers,
          hashtags,
        }
        delete payload.sharedContent
      } else if (
        values.type === FeedType.IMAGE ||
        values.type === FeedType.VIDEO
      ) {
        const existingItems: MediaItem[] = values.mediaItems || []
        const allItems = [...mediaItems, ...existingItems]

        payload = {
          ...values,
          mediaItems: allItems,
          taggedUsers,
          hashtags,
        }
        delete payload.sharedContent
      } else {
        payload = {
          ...values,
          mediaItems: videoUrl ? mediaItems : [],
          sharedContent: values.sharedContent as SharedContent,
          taggedUsers,
          hashtags,
        }
      }
      // Remove undefined fields (type-safe)
      Object.keys(payload).forEach((k) => {
        const key = k as keyof FeedCreatePostPayload
        if (payload[key] === undefined) {
          delete payload[key]
        }
      })

      await axios.post('/feeds', payload as FeedCreatePostPayload)
      toast.success('โพสต์สำเร็จ')
      form.reset(defaultValues)
      onClose()
    } catch (e) {
      toast.error((e as Error)?.message || 'เกิดข้อผิดพลาด')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        aria-describedby=""
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900">
            สร้างโพสต์ใหม่
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <Card className="p-6 bg-white border border-gray-200 shadow-sm">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
                autoComplete="off"
              >
                {/* Content Textarea */}
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium">
                        เขียนอะไรบางอย่าง...
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Textarea
                            maxLength={2000}
                            placeholder="คุณกำลังคิดอะไรอยู่..."
                            className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                            {...field}
                          />
                          <div className="absolute -bottom-[18px] right-3 text-xs text-gray-500">
                            {field.value?.length || 0}/500
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Video Preview */}
                {videoUrl && (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                      <LucideVideo className="w-5 h-5" />
                      วิดีโอที่แชร์
                    </FormLabel>
                    <div className="space-y-3">
                      <div className="relative rounded-lg overflow-hidden bg-black">
                        <video
                          src={videoUrl}
                          className="w-full h-full object-contain"
                          controls
                          preload="metadata"
                        />
                        <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
                          <LucideVideo className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        วิดีโอนี้จะถูกแนบในโพสต์ของคุณ
                      </p>
                    </div>
                  </FormItem>
                )}

                {/* Hashtags */}
                <FormItem>
                  <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                      />
                    </svg>
                    แฮชแท็ก
                  </FormLabel>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        disabled={(form.watch('hashtags') || []).length >= 10}
                        value={hashtagInput}
                        onChange={(e) => setHashtagInput(e.target.value)}
                        placeholder="เพิ่ม แฮชแท็ก..."
                        className="flex-1 h-12 bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleAddHashtag()
                          }
                        }}
                      />
                      <Button
                        disabled={(form.watch('hashtags') || []).length >= 10}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddHashtag}
                        className="bg-blue-500 hover:bg-blue-600 border-blue-400 !text-white px-4 min-h-12"
                      >
                        เพิ่ม
                      </Button>
                    </div>

                    {(form.watch('hashtags') || []).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(form.watch('hashtags') || []).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-sm font-medium text-blue-400 bg-blue-500/20 px-3 py-1 rounded-full hover:bg-blue-500/30 cursor-pointer transition-colors"
                            onClick={() => handleRemoveHashtag(tag)}
                          >
                            #{tag}
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </FormItem>

                {/* Privacy */}
                <FormField
                  control={form.control}
                  name="privacy"
                  render={() => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        ความเป็นส่วนตัว
                      </FormLabel>
                      <FormControl>
                        <div
                          className="grid gap-3"
                          style={{
                            gridTemplateColumns:
                              'repeat(auto-fit, minmax(120px, 1fr))',
                          }}
                        >
                          {Object.values(FeedPrivacy).map((privacy) => (
                            <Button
                              key={privacy}
                              type="button"
                              variant={
                                form.watch('privacy') === privacy
                                  ? 'default'
                                  : 'outline'
                              }
                              size="sm"
                              className={`${
                                form.watch('privacy') === privacy
                                  ? 'bg-blue-500 hover:bg-blue-600 text-white border-blue-500'
                                  : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                              }`}
                              onClick={() => form.setValue('privacy', privacy)}
                            >
                              {privacy === FeedPrivacy.PUBLIC && 'สาธารณะ'}
                              {privacy === FeedPrivacy.FRIENDS &&
                                'เพื่อนเท่านั้น'}
                              {privacy === FeedPrivacy.PRIVATE && 'ส่วนตัว'}
                            </Button>
                          ))}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 min-h-12"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 border-blue-400 !text-white px-6 py-3 min-h-12 flex-1"
                  >
                    {loading ? 'กำลังโพสต์...' : 'โพสต์'}
                  </Button>
                </div>
              </form>
            </Form>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  )
}
